import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ArrowsOutSimple } from '@phosphor-icons/react';

/**
 * Mobile-only floating panel that is:
 *  - Draggable (single-finger drag on the header/title bar)
 *  - Independently resizable in width & height via two-finger pinch anywhere
 *    on the panel body (horizontal spread → width, vertical spread → height).
 *  - Bounded to the viewport so it never disappears off-screen.
 *
 * Kept intentionally lightweight — no external deps.
 *
 * Props:
 *  - title:       string
 *  - onClose:     () => void
 *  - headerColor: bg color hex for the header (default amber)
 *  - initialWidth / initialHeight
 *  - children:    panel body
 *  - storageKey:  optional sessionStorage key so size/pos survives close-open
 */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const FloatingResizablePanel = ({
  title,
  onClose,
  headerColor = '#f59e0b',
  initialWidth = 340,
  initialHeight = 440,
  minWidth = 200,
  minHeight = 220,
  maxWidth,
  maxHeight,
  children,
  storageKey = 'floating_panel',
  testId = 'floating-panel',
}) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 700;
  const _maxW = maxWidth || vw - 16;
  const _maxH = maxHeight || vh - 40;

  // Restore prior size/position from sessionStorage
  const readSaved = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return null;
  };
  const saved = readSaved();

  const startW = clamp(saved?.w || initialWidth, minWidth, _maxW);
  const startH = clamp(saved?.h || initialHeight, minHeight, _maxH);
  const [size, setSize] = useState({ w: startW, h: startH });
  const [pos, setPos] = useState({
    x: clamp(saved?.x != null ? saved.x : (vw - startW) / 2, 4, vw - startW - 4),
    y: clamp(saved?.y != null ? saved.y : 60, 4, vh - startH - 4),
  });

  // Persist size/pos so re-opening the panel restores its layout
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ ...pos, ...size }));
    } catch (_) {}
  }, [pos, size, storageKey]);

  // --- Drag (single-finger on header) ---
  const dragState = useRef(null);
  const onHeaderTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    const t = e.touches[0];
    dragState.current = { startX: t.clientX, startY: t.clientY, startPosX: pos.x, startPosY: pos.y };
  };
  const onHeaderTouchMove = (e) => {
    if (!dragState.current || !e.touches || e.touches.length !== 1) return;
    e.preventDefault();
    const t = e.touches[0];
    const dx = t.clientX - dragState.current.startX;
    const dy = t.clientY - dragState.current.startY;
    setPos({
      x: clamp(dragState.current.startPosX + dx, 4, window.innerWidth - size.w - 4),
      y: clamp(dragState.current.startPosY + dy, 4, window.innerHeight - 40),
    });
  };
  const onHeaderTouchEnd = () => { dragState.current = null; };

  // --- Mouse drag fallback (desktop preview / testing) ---
  const onHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startPosX = pos.x, startPosY = pos.y;
    const onMove = (ev) => {
      setPos({
        x: clamp(startPosX + ev.clientX - startX, 4, window.innerWidth - size.w - 4),
        y: clamp(startPosY + ev.clientY - startY, 4, window.innerHeight - 40),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // --- Pinch resize (two-finger anywhere on the panel) ---
  const pinchState = useRef(null);
  const onPanelTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 2) return;
    const [t1, t2] = e.touches;
    pinchState.current = {
      startDX: Math.abs(t1.clientX - t2.clientX),
      startDY: Math.abs(t1.clientY - t2.clientY),
      startW: size.w,
      startH: size.h,
    };
  };
  const onPanelTouchMove = (e) => {
    if (!pinchState.current || !e.touches || e.touches.length !== 2) return;
    e.preventDefault();
    const [t1, t2] = e.touches;
    const curDX = Math.abs(t1.clientX - t2.clientX);
    const curDY = Math.abs(t1.clientY - t2.clientY);
    const s = pinchState.current;
    // Width scales with horizontal spread, height with vertical spread → truly independent axes.
    const w = clamp(s.startW + (curDX - s.startDX), minWidth, window.innerWidth - 8);
    const h = clamp(s.startH + (curDY - s.startDY), minHeight, window.innerHeight - 8);
    setSize({ w, h });
  };
  const onPanelTouchEnd = (e) => {
    if (!e.touches || e.touches.length < 2) pinchState.current = null;
  };

  // --- Resize handle (bottom-right corner) — traditional drag-to-resize ---
  const resizeState = useRef(null);
  const onResizeHandleStart = useCallback((clientX, clientY) => {
    resizeState.current = { startX: clientX, startY: clientY, startW: size.w, startH: size.h };
  }, [size.w, size.h]);
  const onResizeTouchStart = (e) => {
    e.stopPropagation();
    if (!e.touches || e.touches.length !== 1) return;
    onResizeHandleStart(e.touches[0].clientX, e.touches[0].clientY);
  };
  const onResizeTouchMove = (e) => {
    if (!resizeState.current || !e.touches || e.touches.length !== 1) return;
    e.preventDefault(); e.stopPropagation();
    const t = e.touches[0];
    const dx = t.clientX - resizeState.current.startX;
    const dy = t.clientY - resizeState.current.startY;
    setSize({
      w: clamp(resizeState.current.startW + dx, minWidth, window.innerWidth - 8),
      h: clamp(resizeState.current.startH + dy, minHeight, window.innerHeight - 8),
    });
  };
  const onResizeTouchEnd = () => { resizeState.current = null; };

  const onResizeMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    onResizeHandleStart(e.clientX, e.clientY);
    const onMove = (ev) => {
      if (!resizeState.current) return;
      const dx = ev.clientX - resizeState.current.startX;
      const dy = ev.clientY - resizeState.current.startY;
      setSize({
        w: clamp(resizeState.current.startW + dx, minWidth, window.innerWidth - 8),
        h: clamp(resizeState.current.startH + dy, minHeight, window.innerHeight - 8),
      });
    };
    const onUp = () => {
      resizeState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className="fixed z-[70] shadow-2xl rounded-xl overflow-hidden bg-white flex flex-col select-none"
      style={{
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
      }}
      data-testid={testId}
      onTouchStart={onPanelTouchStart}
      onTouchMove={onPanelTouchMove}
      onTouchEnd={onPanelTouchEnd}
    >
      {/* Header (drag handle) */}
      <div
        className="px-3 py-2 text-white font-bold text-xs uppercase flex items-center justify-between cursor-move touch-none"
        style={{ background: headerColor }}
        onTouchStart={onHeaderTouchStart}
        onTouchMove={onHeaderTouchMove}
        onTouchEnd={onHeaderTouchEnd}
        onMouseDown={onHeaderMouseDown}
        data-testid={`${testId}-header`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="opacity-80">⠿</span>
          <span className="truncate">{title}</span>
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center flex-shrink-0"
          aria-label="Close"
          data-testid={`${testId}-close`}
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      {/* Interaction hint (works on desktop + mobile) */}
      <div className="px-3 py-1 bg-amber-50 text-[10px] text-amber-800 border-b border-amber-200 flex items-center gap-1.5 flex-shrink-0">
        <ArrowsOutSimple size={11} weight="bold" />
        <span>Drag header to move · Drag corner ↘ to resize</span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {children}
      </div>

      {/* Resize handle (bottom-right corner) */}
      <div
        onTouchStart={onResizeTouchStart}
        onTouchMove={onResizeTouchMove}
        onTouchEnd={onResizeTouchEnd}
        onMouseDown={onResizeMouseDown}
        className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-end justify-end pr-1 pb-1 touch-none"
        style={{
          background: 'transparent',
        }}
        data-testid={`${testId}-resize-handle`}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M13 5v8H5" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13 9v4H9" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
};

export default FloatingResizablePanel;
